import React, { useContext, useState, createContext, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AttachmentsUploadStore } from './attachments-upload-store';
import { useStream, createStreamListener } from '../socket/use-stream';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useWorkspaceWindowType } from '../common/hooks/use-workspace-window-type';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { ATTACHMENTS_QUERY_KEY } from './constants';

const AttachmentsUploadStoreContext = createContext<AttachmentsUploadStore | undefined>(undefined);

export function AttachmentsUploadStoreProvider({ children }: { children: React.ReactNode }) {
    const [store] = useState(() => new AttachmentsUploadStore());
    const queryClient = useQueryClient();
    const { isCadElectron, isFirstResponder } = useApplicationType();
    const windowType = useWorkspaceWindowType();
    const { mediaAttachmentsEnabled } = useEnabledFeatures();

    // Socket listener for attachment creation events
    const listener = useMemo(
        function createAttachmentsUploadListener() {
            if (!mediaAttachmentsEnabled) {
                return;
            }
            // Only listen in EVENT_V2 window for CAD Electron, or always listen for web CAD and FR
            if (isCadElectron && !isFirstResponder && windowType !== 'EVENT_V2') {
                return;
            }

            return createStreamListener({
                streamNames: ['cadAttachments'] as const,
                onMessage: (message) => {
                    if (message.streamName === 'cadAttachments') {
                        // Handle multiple attachments in the response
                        const uniqueEntityIds = new Set<number>();
                        message.payload.attachments.forEach((attachment) => {
                            uniqueEntityIds.add(attachment.entityId);
                            if (
                                attachment.attachmentLogType === 'CREATE' &&
                                attachment.fileServerFileId
                            ) {
                                store.handleUploadCompletionByFileServerFileId({
                                    fileServerFileId: attachment.fileServerFileId,
                                    success: true,
                                });
                            }
                        });
                        uniqueEntityIds.forEach((entityId) => {
                            // Invalidate attachments query to refresh the list
                            queryClient.invalidateQueries({
                                queryKey: [ATTACHMENTS_QUERY_KEY, entityId],
                            });
                        });
                    }
                },
            });
        },
        [store, queryClient, isCadElectron, isFirstResponder, windowType, mediaAttachmentsEnabled]
    );

    useStream(listener);

    return (
        <AttachmentsUploadStoreContext.Provider value={store}>
            {children}
        </AttachmentsUploadStoreContext.Provider>
    );
}

export function useAttachmentsUploadStore(): AttachmentsUploadStore {
    const store = useContext(AttachmentsUploadStoreContext);
    if (!store) {
        throw new Error('Unexpectedly did not find attachments upload store');
    }
    return store;
}
